import type { RankedPlace } from '@/hooks/useLLM';

function sortByPracticalityThenRating(a: RankedPlace, b: RankedPlace): number {
  const practA = a.practicalityRank ?? 0;
  const practB = b.practicalityRank ?? 0;
  if (practA !== practB) {
    return practB - practA;
  }
  return (b.rating ?? 0) - (a.rating ?? 0);
}

export function balanceByVibes(places: RankedPlace[], vibes: string[]): RankedPlace[] {
  if (vibes.length <= 1 || places.length === 0) {
    return [...places].sort(sortByPracticalityThenRating);
  }

  const targetPerVibe = Math.ceil(places.length / vibes.length);
  const vibeCounters: Record<string, number> = {};
  vibes.forEach(v => { vibeCounters[v] = 0; });

  const sortedByPracticality = [...places].sort(sortByPracticalityThenRating);
  const selected: RankedPlace[] = [];
  const remaining: RankedPlace[] = [];

  for (const place of sortedByPracticality) {
    const matchingVibes = (place.matchingVibes || []).filter(v => v && v.trim());
    const underrepresentedVibe = matchingVibes.find(v =>
      vibes.includes(v) && vibeCounters[v] < targetPerVibe
    );

    if (underrepresentedVibe) {
      selected.push(place);
      matchingVibes.forEach(v => {
        if (vibes.includes(v)) {
          vibeCounters[v]++;
        }
      });
    } else {
      remaining.push(place);
    }
  }

  const allVibesHaveMinimum = vibes.every(v => vibeCounters[v] >= 1);

  if (!allVibesHaveMinimum) {
    for (const vibe of vibes) {
      if (vibeCounters[vibe] === 0) {
        const vibeMatch = remaining.find(p =>
          p.matchingVibes?.includes(vibe) && !selected.includes(p)
        );
        if (vibeMatch) {
          const idx = remaining.indexOf(vibeMatch);
          remaining.splice(idx, 1);
          selected.push(vibeMatch);
          vibeMatch.matchingVibes?.forEach(v => {
            if (vibes.includes(v)) {
              vibeCounters[v]++;
            }
          });
        }
      }
    }
  }

  const balanced = [...selected, ...remaining];
  return balanced.sort(sortByPracticalityThenRating);
}
